import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import Vue3TouchEvents, { type Vue3TouchEventsOptions } from 'vue3-touch-events'
import './assets/styles/tailwind.css'
import './assets/styles/main.scss'
import FloatingVue, { Tooltip } from 'floating-vue'
import 'floating-vue/dist/style.css'
import * as Sentry from '@sentry/vue'
import { DevTools, FormatSimple, Tolgee, VueTolgee } from '@tolgee/vue'
import { FormatIcu } from '@tolgee/format-icu'
import { env } from '@/env'
import { createGtm } from '@gtm-support/vue-gtm'
import { initialize } from '@/composables/init'
import { useNewLoader } from '@/config/new-loader'
import { sleep } from '@/services/helper'
import { initializeBaseMiniApp } from '@/services/base'

const WebApp = Telegram.WebApp
const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatSimple())
  .use(FormatIcu())
  // .use(BackendFetch({ prefix: 'https://cdn.tolg.ee/7af4761aa233c8f31d31809b1c45ea00' }))
  .init({
    defaultNs: 'common',

    // for development
    apiUrl: import.meta.env.VITE_TOLGEE_API_URL,
    apiKey: import.meta.env.VITE_TOLGEE_API_KEY,

    defaultLanguage: 'en',
    availableLanguages: ['en', 'fr'],

    // for production
    staticData: {
      'en:common': () => import('@/assets/i18n/common/en.json').then(f => f.default),
      'fr:common': () => import('@/assets/i18n/common/fr.json').then(f => f.default),
    },
  })

const app = createApp(App)

if (env.sentryDsn && [6201325962, 6307278703, 147906258].includes(WebApp.initDataUnsafe.user?.id ?? 0)) {
  Sentry.init({
    dsn: env.sentryDsn,
    integrations: [
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
          hooks: ['create', 'mount', 'update'],
        },
      }),
      Sentry.browserTracingIntegration(),
      Sentry.extraErrorDataIntegration({
        depth: 8,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, 503],
        failedRequestTargets: [
          new RegExp('^https://dropee.clicker-game-api.tropee.com/api/'),
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          new RegExp('^https://dropee.clicker-game-api.tropee.com/api/'),
        ],
      }),
    ],
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,
    tracesSampleRate: 1.0,
    app: app,
  })
}
app.use(VueTolgee, { tolgee })
if (env.gtmId) {
  app.use(
    createGtm({
      id: env.gtmId,
      defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
      compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
      loadScript: true,
      vueRouter: router,
      ignoredViews: [],
      trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
    }),
  )
}

app.use<Vue3TouchEventsOptions>(Vue3TouchEvents, {
  disableClick: false,
  tapTolerance: 30,
})
app.use(FloatingVue, {
  themes: {
    'tooltip': {
      triggers: ['touch', 'click', 'hover'],
      popperTriggers: ['touch', 'click', 'hover'],
      html: true,
      autoHide: true,
      distance: 10,
    },
    'dropdown': {
      arrow: false,
    },
  },
})
app.component('VTooltip', Tooltip)
app.use(router)

async function initializeApp() {
  await initializeBaseMiniApp()
  if (useNewLoader) {
    await Promise.all([
      initialize(),
      sleep(1000),
    ])
  }
  app.mount('#app')
}

initializeApp().then()
